import React, { useState, useContext, useEffect, useMemo } from "react";
import './PolymatchReg.css'; // Make sure to link the CSS file
import { UserContext } from '../UserContext';
import StudentNav from '../Navbars/StudentNav';
import AdminNav from '../Navbars/AdminNav';
import TeacherNav from '../Navbars/TeacherNav';
import Footer from "../Footer";
import { useNavigate, useSearchParams } from 'react-router-dom/dist/umd/react-router-dom.development';
import Select from 'react-select'; // Import react-select
import countryList from 'react-select-country-list'; // Import react-select-country-list
import { FaEnvelope, FaExclamationCircle, FaUser, FaMapMarkerAlt, FaGraduationCap, FaCamera, FaPhone, FaBuilding, FaTimes } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imageCompression from 'browser-image-compression'; // Import the image compression library
import apiService from '../../services/apiService';
import styles from "./Polymatch.module.css";
// Import images for the header section
import polymatchLogo from '../../assets/polymatchlogo.png';
import poly1 from '../../assets/login1.png';
import poly2 from '../../assets/login2.png';
// Import image for the mid-section
import polymatchImage from '../../assets/Polymatch.png';
import { Link } from "react-router-dom";

const PolymatchReg = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    parentName: '',
    whatsapp: '',
    email: '',
    address: '',
    city: '',
    country: '',
    school: '',
    childPhoto: null,
    acceptTerms: true, // New field for terms acceptance
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null); // State for image preview
  const [activeCompetition, setActiveCompetition] = useState(null); // State for the single, active competition
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false); // State for terms popup

  const { isAdmin, isTeacher, isStudent, setUserRole, setUserInfo, userInfo } = useContext(UserContext);

  const username = userInfo?.username;
  const _avatar_ = userInfo?.avatar;

  const renderNavbar = () => {
    if (isAdmin) {
      return <AdminNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    } else if (isTeacher) {
      return <TeacherNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    } else if (isStudent) {
      return <StudentNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userInfo = await apiService.getCurrentUser();
        setUserInfo(userInfo);
        if (userInfo?.usertype) {
          setUserRole(userInfo?.usertype);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    const fetchCompetitionData = async () => {
      try {
        const allCompetitions = await apiService.getPolymatchCompetition();
        if (Array.isArray(allCompetitions) && allCompetitions.length > 0) {
          // Find the competition with the highest ID
          const latestCompetition = allCompetitions.reduce(
            (max, comp) => (max.Competition_Id > comp.Competition_Id ? max : comp)
          );
          setActiveCompetition(latestCompetition);
        } else {
          // Handle case where a single object is returned for backward compatibility
          setActiveCompetition(allCompetitions);
        }
      } catch (error) {
        console.error('Error fetching competition data:', error);
      }
    };

    fetchData();
    fetchCompetitionData();
  }, [isAdmin, isTeacher, isStudent]);

  const onLogout = async () => {
    try {
      await apiService.logout();
      setUserInfo(null);
      setUserRole('guest');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: name === 'country'
        ? (selectedOption ? selectedOption.label : '')
        : (selectedOption ? selectedOption.value : ''),
    });
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        childPhoto: 'Please upload a valid image file (JPEG, PNG)'
      }));
      return;
    }

    // Validate file size (200MB max)
    if (file.size > 200 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        childPhoto: 'Image size should be less than 200MB'
      }));
      return;
    }

    try {
      const options = {
        maxSizeMB: 1, // 1MB for better performance
        maxWidthOrHeight: 1024,
        useWebWorker: true
      };

      const compressedFile = await imageCompression(file, options);
      // Wrap in a File object with a proper name/type
      const finalFile = new File([compressedFile], `child_${Date.now()}_${formData.firstName || 'photo'}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      setFormData(prev => ({
        ...prev,
        childPhoto: finalFile
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(finalFile);

      // Clear any previous errors
      setErrors(prev => ({
        ...prev,
        childPhoto: null
      }));
    } catch (error) {
      console.error('Error compressing image:', error);
      setErrors(prev => ({
        ...prev,
        childPhoto: 'Error processing image. Please try again.'
      }));
    }
  };

  const validateForm = () => {
    console.log('Validating form data:', formData);
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First Name must be at least 2 characters";
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = "First Name must be less than 50 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = "First Name can only contain letters and spaces";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last Name must be at least 2 characters";
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = "Last Name must be less than 50 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = "Last Name can only contain letters and spaces";
    }

    // Age validation
    if (!formData.age) {
      newErrors.age = "Age is required";
    } else {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 4 || ageNum > 16) {
        newErrors.age = "Age must be between 4 and 16 years";
      }
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    // Parent Name validation
    if (!formData.parentName.trim()) {
      newErrors.parentName = "Parent Name is required";
    } else if (formData.parentName.trim().length < 2) {
      newErrors.parentName = "Parent Name must be at least 2 characters";
    } else if (formData.parentName.trim().length > 100) {
      newErrors.parentName = "Parent Name must be less than 100 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.parentName.trim())) {
      newErrors.parentName = "Parent Name can only contain letters and spaces";
    }

    // WhatsApp validation
    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = "WhatsApp number is required";
    } else if (!/^\+?[0-9]{10,15}$/.test(formData.whatsapp.trim().replace(/\s+/g, ''))) {
      newErrors.whatsapp = "Please enter a valid WhatsApp number (10-15 digits)";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    } else if (formData.email.trim().length > 100) {
      newErrors.email = "Email must be less than 100 characters";
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    } else if (formData.address.trim().length < 10) {
      newErrors.address = "Address must be at least 10 characters";
    } else if (formData.address.trim().length > 200) {
      newErrors.address = "Address must be less than 200 characters";
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    } else if (formData.city.trim().length < 2) {
      newErrors.city = "City must be at least 2 characters";
    } else if (formData.city.trim().length > 50) {
      newErrors.city = "City must be less than 50 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.city.trim())) {
      newErrors.city = "City can only contain letters and spaces";
    }

    // Country validation
    if (!formData.country || formData.country.trim() === '') {
      newErrors.country = "Country is required";
    }

    // School validation (optional but with constraints if provided)
    if (formData.school && typeof formData.school === 'string') {
      if (formData.school.trim().length > 0 && formData.school.trim().length < 2) {
        newErrors.school = "School name must be at least 2 characters";
      } else if (formData.school.trim().length > 100) {
        newErrors.school = "School name must be less than 100 characters";
      }
    }

    // Photo validation
    if (!formData.childPhoto) {
      newErrors.childPhoto = "Child's photo is required";
    }

    // Terms and conditions validation
    // if (!formData.acceptTerms) {
    //   newErrors.acceptTerms = "You must accept the terms and conditions to register";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Run full form validation first
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly', { position: 'top-center' });
      setIsSubmitting(false);
      return;
    }

    // Additional age validation (company only allows 4-15)
    const ageNum = Number(formData.age);
    console.log('Submitting form with age:', ageNum, 'Raw value:', formData.age);
    if (!ageNum || isNaN(ageNum) || ageNum < 4 || ageNum > 16) {
      toast.error('Only ages 4 to 16 are allowed for this competition.', { position: 'top-center' });
      setIsSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('lastName', formData.lastName);
    formDataToSend.append('age', ageNum);
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('parentName', formData.parentName);
    formDataToSend.append('whatsapp', formData.whatsapp);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('country', formData.country);
    formDataToSend.append('school', formData.school);
    formDataToSend.append('childPhoto', formData.childPhoto);
    if (activeCompetition && activeCompetition.Competition_Id) {
      formDataToSend.append('Competition_Id', activeCompetition.Competition_Id);
    } else {
      toast.error("No active competition found. Cannot register.", { position: "top-center" });
      setIsSubmitting(false);
      return;
    }

    try {
      const responseData = await apiService.registerPolymatch(formDataToSend);

      // Check for the success message from the backend's JSON response
      if (responseData && responseData.message === 'Registration successful') {
        toast.success(
          'Thank you for registering for the competition. A member of our team will contact you shortly through the given WhatsApp number to provide payment details.',
          { position: 'top-center', autoClose: 5000 }
        );
        setFormData({
          firstName: '', lastName: '', age: '', gender: '', parentName: '',
          whatsapp: '', email: '', address: '', city: '', country: '',
          school: '', childPhoto: null, acceptTerms: true,
        });
        setImagePreview(null);
      } else {
        // Handle cases where the server returns 200 OK but with an error message
        toast.error(responseData.message || 'Registration failed.', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error submitting the form:', error);
      // This will catch network errors or errors thrown from apiService for non-ok responses
      toast.error(error.message || 'An error occurred during registration.', { position: 'top-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" }
  ];

  const countryOptions = useMemo(() => countryList().getData(), []);

  const selectStyles = {
    control: (base) => ({
      ...base,
      background: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
      minHeight: '58px',
      borderRadius: '18px',
      padding: '0 17px',
      '&:hover': {
        borderColor: '#DCDCDC'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      background: 'transparent !important',
      padding: '0 !important',
    }),
    singleValue: (base) => ({
      ...base,
      background: 'transparent !important',
      color: '#333 !important',
    }),
    placeholder: (base) => ({
      ...base,
      background: 'transparent !important',
      color: '#888 !important',
    }),
    input: (base) => ({
      ...base,
      background: 'transparent !important',
      color: '#333 !important',
      padding: '0 !important',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none !important',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#888 !important',
    }),
  };

  // Terms and Conditions Popup Component
  const TermsPopup = () => {
    if (!showTermsPopup) return null;

    return (
      <div className="PolymatchReg-termsPopupOverlay">
        <div className="PolymatchReg-termsPopup">
          <div className="PolymatchReg-termsPopupHeader">
            <h2>PolyMatch Global Competitions – Terms & Conditions</h2>
            <button
              className="PolymatchReg-termsPopupClose"
              onClick={() => setShowTermsPopup(false)}
            >
              <FaTimes />
            </button>
          </div>
          <div className="PolymatchReg-termsPopupContent">
            <p>By registering for and participating in PolyMatch Global Competitions, organized under the umbrella of Polymath-Kids, you agree to the following Terms & Conditions:</p>

            <h3>1. Eligibility</h3>
            <p>Competitions are open to children within the specified age groups (4–16 years) at the time of registration.</p>
            <p>Registration must be completed by a parent/guardian or an authorized school representative.</p>

            <h3>2. Registration & Fees</h3>
            <p>Registration is confirmed only after payment of the competition fee.</p>
            <p>Fees are non-refundable, except in cases where the competition is cancelled by Polymath-Kids.</p>
            <p>One registration per participant per competition category is allowed.</p>

            <h3>3. Competition Rules</h3>
            <p>Participants must log in with their official PolyMatch credentials at the scheduled time.</p>
            <p>Any form of cheating, impersonation, or use of unfair means will result in immediate disqualification.</p>
            <p>The competition will be conducted in English (or other specified languages where applicable).</p>

            <h3>4. Technology & Access</h3>
            <p>Competitions are conducted online; participants are responsible for arranging their own device, stable internet connection, and environment.</p>
            <p>Polymath-Kids is not responsible for disruptions due to technical issues on the participant's end.</p>

            <h3>5. Evaluation & Results</h3>
            <p>All results are final and based on automated systems and/or independent judges' evaluation (depending on the category).</p>
            <p>No correspondence regarding scoring or ranking will be entertained.</p>
            <p>Certificates, medals, and trophies (where applicable) will be awarded as per the announced criteria.</p>

            <h3>6. Data Protection & Privacy</h3>
            <p>Participant details (name, age, country, school, etc.) may be published on leaderboards, certificates, and promotional material.</p>
            <p>All personal data will be handled in accordance with Polymath-Kids' Privacy Policy.</p>

            <h3>7. Intellectual Property</h3>
            <p>All competition content (questions, challenges, platforms, etc.) is the intellectual property of Polymath-Kids and cannot be copied, distributed, or reused without permission.</p>

            <h3>8. Code of Conduct</h3>
            <p>Parents/guardians must ensure that children participate ethically and independently.</p>
            <p>Respectful online behavior is mandatory; any misconduct may lead to disqualification.</p>

            <h3>9. Liability</h3>
            <p>Polymath-Kids shall not be held liable for any loss, damage, or injury incurred during participation.</p>
            <p>Participation is voluntary and at the sole discretion of the parent/guardian.</p>

            <h3>10. Amendments</h3>
            <p>Polymath-Kids reserves the right to amend the Terms & Conditions, competition format, or schedule at any time.</p>
            <p>Updates will be communicated via the official website and email.</p>
          </div>
          <div className="PolymatchReg-termsPopupFooter">
            <button
              className="PolymatchReg-termsPopupAgree"
              onClick={() => {
                setFormData(prev => ({ ...prev, acceptTerms: true }));
                setShowTermsPopup(false);
              }}
            >
              I Agree to Terms
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderNavbar()}
      <section className={styles.headerSection}>
        <div className={styles.headerContainer}>
          <img
            src={polymatchLogo}
            alt="Polymatch Logo"
            className={styles.polymatchLogo}
          />
          <h1 className="PolymatchReg-newHeaderHeading">
            <span className="PolymatchReg-newHeaderHeadingPart1">Register for the </span><br />
            <span className="PolymatchReg-newHeaderHeadingPart2">
              {activeCompetition?.Competition_Name || 'Knowledge Competition 2025'}
            </span>
          </h1>
          <br></br>
          <h1 className={styles.headerHeading}>
            Please fill out the form below with your child's accurate full name, grade, and school details as these will appear on the certificate. Include parent or guardian contact information for communication purposes. Ensure all school/institution details are correct, and review everything before submission. By registering, you confirm that the information provided is true and you consent to participation in the competition.
          </h1>

          <div className={styles.headerButtons}>
            {/* <Link to="/PolymatchReg" className={styles.registerBtn}>Register</Link> */}
            <Link to="/PolymatchDetail" className={styles.detailsBtn}>Details for Competition</Link>
          </div>
          <img src={poly1} alt="Poly 1" className={styles.poly1Img} />
          <img src={poly2} alt="Poly 2" className={styles.poly2Img} />
        </div>
      </section>
      {/* <section className="PolymatchReg-headerSection"> 
        <div className="PolymatchReg-headerContainer">
          <img
                      src={polymatchLogo}
                      alt="Polymatch Logo"
                      className={styles.polymatchLogo}
                    />
          <h1 className="PolymatchReg-newHeaderHeading"> 
            <span className="PolymatchReg-newHeaderHeadingPart1">Register for the </span><br />
            <span className="PolymatchReg-newHeaderHeadingPart2">
              {activeCompetition?.Competition_Name || 'Knowledge Competition 2025'}
            </span>
          </h1>
          <p className="PolymatchReg-headerParagraph"> 
            Please fill out the form below with your child's accurate full name, grade, and school details as these will appear on the certificate. Include parent or guardian contact information for communication purposes. Ensure all school/institution details are correct, and review everything before submission. By registering, you confirm that the information provided is true and you consent to participation in the competition.
          </p>
          
          <img src={poly1} alt="Poly 1" className="PolymatchReg-poly1Img" />
          <img src={poly2} alt="Poly 2" className="PolymatchReg-poly2Img" />
        </div>
      </section> */}

      <section className="PolymatchReg-formSection">
        {/* <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div> */}

        <div className="PolymatchReg-midImageContainer">
          <img
            src={polymatchLogo}
            alt="Polymatch Logo"
            className={styles.polymatchLogo}
          />
        </div>

        {/* <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div> */}

        <h2 className="PolymatchReg-formHeading">
          <span className="PolymatchReg-formHeadingPart1">Register for the </span><br />
          <span className="PolymatchReg-formHeadingPart2">
            {activeCompetition?.Competition_Name || 'Knowledge Competition 2025'}
          </span>
        </h2>

        {/* Competition Display Section */}
        {activeCompetition && (
          <div className="PolymatchReg-competitionDisplay">
            <div className="PolymatchReg-competitionInfo">
              <h3 className="PolymatchReg-competitionTitle">
                Selected Competition: {activeCompetition.Competition_Name}
              </h3>
              <p className="PolymatchReg-competitionDate">
                Last Date to Register: {new Date(activeCompetition.Competition_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',

                })}
              </p>
            </div>
          </div>
        )}

        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>

        <div className="PolymatchReg-formContainer">
          <ToastContainer position="top-center" autoClose={3000} pauseOnFocusLoss={false} draggable pauseOnHover={false} />
          <form onSubmit={handleSubmit} className="PolymatchReg-signupForm">
            <div className="PolymatchReg-formGrid">
              {/* Child Photo Upload Field */}
              <div className="PolymatchReg-photoUploadContainer">
                <div className="PolymatchReg-formGroupPhotoField">
                  <input
                    type="file"
                    id="childPhoto"
                    name="childPhoto"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="childPhoto" className="PolymatchReg-photoUploadLabel">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <i className="fas fa-upload PolymatchReg-uploadIcon"></i>
                    )}
                  </label>
                </div>
                <span className="PolymatchReg-uploadText">Upload Child's picture <span style={{ color: 'red' }}>*</span></span>
                {errors.childPhoto && (
                  <div className="PolymatchReg-photoError">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.childPhoto}
                  </div>
                )}
              </div>

              {/* Parent Name */}
              <div className="PolymatchReg-formGroupParentNameField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="parentName"
                    name="parentName"
                    placeholder="Parent Name *"
                    value={formData.parentName}
                    onChange={handleChange}
                    className={errors.parentName ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                  <i className="fas fa-user PolymatchReg-inputIcon"></i>
                </div>
                {errors.parentName && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.parentName}
                  </div>
                )}
              </div>

              {/* First Name */}
              <div className="PolymatchReg-formGroupFirstNameField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="First Name *"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                  <i className="fas fa-user PolymatchReg-inputIcon"></i>
                </div>
                {errors.firstName && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.firstName}
                  </div>
                )}
              </div>

              {/* Last Name */}
              <div className="PolymatchReg-formGroupLastNameField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name *"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                  <i className="fas fa-user PolymatchReg-inputIcon"></i>
                </div>
                {errors.lastName && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.lastName}
                  </div>
                )}
              </div>

              {/* Gender */}
              <div className="PolymatchReg-formGroupGenderField">
                <Select
                  name="gender"
                  placeholder="Select Gender *"
                  value={genderOptions.find(option => option.value === formData.gender)}
                  options={genderOptions}
                  onChange={option => handleSelectChange('gender', option)}
                  isDisabled={isSubmitting}
                  className={errors.gender ? 'PolymatchReg-errorSelect' : ''}
                  classNamePrefix="PolymatchReg-react-select"
                />
                {errors.gender && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.gender}
                  </div>
                )}
              </div>

              {/* Age */}
              <div className="PolymatchReg-formGroupAgeField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="number"
                    id="age"
                    name="age"
                    placeholder="Age of Child (4-16) *"
                    value={formData.age}
                    onChange={handleChange}
                    className={errors.age ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    min="4"
                    max="16"
                  />
                  <i className="fas fa-birthday-cake PolymatchReg-inputIcon"></i>
                </div>
                {errors.age && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.age}
                  </div>
                )}
              </div>

              {/* WhatsApp */}
              <div className="PolymatchReg-formGroupWhatsappNumberField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="tel"
                    id="whatsapp"
                    name="whatsapp"
                    placeholder="WhatsApp Number *"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className={errors.whatsapp ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={15}
                    onKeyDown={(event) => {
                      if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Tab') {
                        event.preventDefault();
                      }
                    }}
                  />
                  <i className="fab fa-whatsapp PolymatchReg-inputIcon"></i>
                </div>
                {errors.whatsapp && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.whatsapp}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="PolymatchReg-formGroupEmailField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                  <i className="fas fa-envelope PolymatchReg-inputIcon"></i>
                </div>
                {errors.email && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="PolymatchReg-formGroupLocationField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="address"
                    name="address"
                    placeholder="Address *"
                    value={formData.address}
                    onChange={handleChange}
                    className={errors.address ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={200}
                  />
                  <i className="fas fa-map-marker-alt PolymatchReg-inputIcon"></i>
                </div>
                {errors.address && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.address}
                  </div>
                )}
              </div>

              {/* City */}
              <div className="PolymatchReg-formGroupCityField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="City *"
                    value={formData.city}
                    onChange={handleChange}
                    className={errors.city ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                  <i className="fas fa-city PolymatchReg-inputIcon"></i>
                </div>
                {errors.city && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.city}
                  </div>
                )}
              </div>

              {/* School */}
              <div className="PolymatchReg-formGroupSchoolField">
                <div className="PolymatchReg-inputWrapper">
                  <input
                    type="text"
                    id="school"
                    name="school"
                    placeholder="School"
                    value={formData.school}
                    onChange={handleChange}
                    className={errors.school ? `PolymatchReg-error` : ""}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                  <i className="fas fa-school PolymatchReg-inputIcon"></i>
                </div>
                {errors.school && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.school}
                  </div>
                )}
              </div>

              {/* Country */}
              <div className="PolymatchReg-formGroupCountryField">
                <Select
                  name="country"
                  placeholder="Choose your country *"
                  value={countryOptions.find(option => option.value === formData.country)}
                  options={countryOptions}
                  onChange={option => handleSelectChange('country', option)}
                  isDisabled={isSubmitting}
                  className={errors.country ? 'PolymatchReg-errorSelect' : ''}
                  classNamePrefix="PolymatchReg-react-select"
                />
                {errors.country && (
                  <div className="PolymatchReg-errorMessage">
                    <i className="fas fa-exclamation-circle"></i>
                    {errors.country}
                  </div>
                )}
              </div>
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="PolymatchReg-termsContainer">
              <label className="PolymatchReg-termsLabel">
                {/* <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="PolymatchReg-termsCheckbox"
                /> */}
                <span className="PolymatchReg-termsText">
                  By Registering I agree to the {' '}
                  <button
                    type="button"
                    className="PolymatchReg-termsLink"
                    onClick={() => navigate('/polymatchtermandservices')}
                    disabled={isSubmitting}
                  >
                    Terms & Conditions
                  </button>
                  <span style={{ color: 'red' }}>*</span>
                </span>
              </label>
              {errors.acceptTerms && (
                <div className="PolymatchReg-errorMessage">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.acceptTerms}
                </div>
              )}
            </div>

            {/* Note */}
            <div className="PolymatchReg-form-note">
              Note: If Registration form doesn't load/work, please inform us on WhatsApp (+92 3266699907)(+47 47650619), our representative will register your profile
            </div>

            <div className="PolymatchReg-buttonContainer">
              <button type="submit" className="PolymatchReg-signupButton" disabled={isSubmitting}>
                {isSubmitting ? 'Registering...' : 'Register '}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Terms and Conditions Popup */}
      {/* <TermsPopup /> */}

      <Footer />
    </>
  );
};

export default PolymatchReg;